/**
 * @fileoverview Mock-only state behind the My Page write endpoints.
 * @description The shared Target store has no place for what the backend's My Page commands add: a
 * per-target revision (the `tg1.` version a write must quote), when payment details were sent, an
 * open reassignment request, and the dated history of stage changes, reopenings and sends. This
 * module keeps those in memory, keyed by target id, so my-page-mock.service.ts can project them
 * into rows and details and my-page-actions-mock.service.ts can change them. Nothing here is
 * persisted: a page reload resets it, like the rest of the mock stores. A no-op for the real
 * backend, which never loads it.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import type { MyPageTargetEvent } from '@/features/sales/types/my-page.types'

/** @description An open Executive request to hand a target to a colleague. */
export interface MockPendingReassignment {
  approvalRequestId: string
  proposedAssigneeId: string
  requestedById: string
  requestedAt: string
  reason: string
}

/** @description How many times each target has been changed through a My Page command. */
const revisions = new Map<string, number>()

/**
 * @description When payment details were first sent, per target: a time once sent, null once
 * cleared. A target with no entry follows the shared store (an Awaiting Payment target counts as
 * sent, as the seeded ones always did).
 */
const paymentSentAtByTarget = new Map<string, string | null>()

/** @description Each target's open reassignment request, if it has one. */
const pendingByTarget = new Map<string, MockPendingReassignment>()

/** @description The history entries commands added, oldest first, per target. */
const eventsByTarget = new Map<string, MyPageTargetEvent[]>()

/** @description The next number used to mint a reassignment request id. */
let nextRequestNumber = 1

/**
 * @description How many times a target has been changed through a My Page command.
 * @param {string} targetId - The target.
 * @returns {number} Its revision (0 until first changed).
 */
export function revisionOf(targetId: string): number {
  return revisions.get(targetId) ?? 0
}

/**
 * @description Records that a command changed a target, so its version moves on.
 * @param {string} targetId - The target.
 */
export function bumpRevision(targetId: string): void {
  revisions.set(targetId, revisionOf(targetId) + 1)
}

/**
 * @description When a target's payment details were sent, as explicitly recorded.
 * @param {string} targetId - The target.
 * @returns {string | null | undefined} The time sent, null when cleared, undefined when nothing
 * was ever recorded.
 */
export function recordedPaymentSentAt(targetId: string): string | null | undefined {
  return paymentSentAtByTarget.get(targetId)
}

/**
 * @description Records when a target's payment details were sent, or clears it.
 * @param {string} targetId - The target.
 * @param {string | null} sentAt - The time sent, or null to clear it.
 */
export function recordPaymentSentAt(targetId: string, sentAt: string | null): void {
  paymentSentAtByTarget.set(targetId, sentAt)
}

/**
 * @description A target's open reassignment request.
 * @param {string} targetId - The target.
 * @returns {MockPendingReassignment | undefined} The request, if one is open.
 */
export function pendingReassignmentOf(targetId: string): MockPendingReassignment | undefined {
  return pendingByTarget.get(targetId)
}

/**
 * @description Opens a reassignment request for a target.
 * @param {string} targetId - The target.
 * @param {Omit<MockPendingReassignment, 'approvalRequestId' | 'requestedAt'>} request - The
 * proposed assignee, who asked and why.
 * @returns {MockPendingReassignment} The request, with its minted id and time.
 */
export function openPendingReassignment(
  targetId: string,
  request: Omit<MockPendingReassignment, 'approvalRequestId' | 'requestedAt'>,
): MockPendingReassignment {
  const opened: MockPendingReassignment = {
    ...request,
    approvalRequestId: `mock-approval-${String(nextRequestNumber)}`,
    requestedAt: new Date().toISOString(),
  }
  nextRequestNumber += 1
  pendingByTarget.set(targetId, opened)
  return opened
}

/**
 * @description Cancels a target's open reassignment request, as the backend does when the target
 * is closed, reopened or completed.
 * @param {string} targetId - The target.
 */
export function cancelPendingReassignment(targetId: string): void {
  pendingByTarget.delete(targetId)
}

/**
 * @description The history entries commands added to a target.
 * @param {string} targetId - The target.
 * @returns {MyPageTargetEvent[]} The entries, oldest first.
 */
export function loggedEventsOf(targetId: string): MyPageTargetEvent[] {
  return eventsByTarget.get(targetId) ?? []
}

/**
 * @description Adds an entry to a target's history.
 * @param {string} targetId - The target.
 * @param {Omit<MyPageTargetEvent, 'eventId' | 'createdAt'>} event - The entry, without its id and
 * time.
 */
export function logTargetEvent(
  targetId: string,
  event: Omit<MyPageTargetEvent, 'eventId' | 'createdAt'>,
): void {
  const existing = loggedEventsOf(targetId)
  eventsByTarget.set(targetId, [
    ...existing,
    {
      ...event,
      eventId: `${targetId}-event-${String(existing.length + 1)}`,
      createdAt: new Date().toISOString(),
    },
  ])
}

/**
 * @description Forgets everything recorded, so each test starts from the seeded stores.
 */
export function resetMyPageMockState(): void {
  revisions.clear()
  paymentSentAtByTarget.clear()
  pendingByTarget.clear()
  eventsByTarget.clear()
  nextRequestNumber = 1
}
